import { getRetailerIdIssueProposal, useQuery } from '@common/utils';
import { BankIdLogin } from '../components/bank-id-login';
import { bankIdApi } from '../store/bank-id-api';
import { getIamLoginUrl, shouldUseIamFlowForCurrentHost } from '../utils/iam-login-url';
import { sanitizeRetailerId } from '@common/utils';
import { authenticateComplete, changeRetailerId, setRememberRetailerId } from '../store/login/login.actions';
const {
  useCallback,
  useEffect,
  useRef,
  useState
} = React;
const {
  useDispatch,
  useSelector
} = ReactRedux;
const {
  useHistory
} = ReactRouterDOM;
const logger = svs.core.log.getLogger('welcome:BankIdLoginContainer');
const getErrorMessage = ({
  errorCode,
  isThisDevice,
  retailerId
}) => {
  switch (errorCode) {
    case 16:
      {
        const issueProposal = getRetailerIdIssueProposal(retailerId);
        return `Det gick inte att hitta någon användare med ditt personnummer för detta ombud. ${issueProposal}`;
      }
    case 29:
      return 'Ditt personnummer är kopplat till flera användare för detta ombud, be din ombudsadministratör att ta bort dubletterna.';
    case 41:
      logger.error(`Bank-id polling failed with errorCode '${errorCode}'`);
      return 'Ett okänt fel inträffade.';
    case 42:
      logger.error(`Bank-id polling failed with errorCode '${errorCode}' due to unknown autoStartToken.`);
      return 'Ett okänt fel inträffade, försök igen.';
    case 45:
      return 'Det BankID du försöker använda är för gammalt eller spärrat. Använd ett annat BankID eller hämta ett nytt hos din internetbank.';
    case 47:
      logger.error(`Bank-id polling failed with errorCode '${errorCode}'`);
      return 'Internt tekniskt fel. Försök igen.';
    case 49:
      return 'BankID-appen svarar inte. Kontrollera att den är startad och att du har internetanslutning. Om du inte har något giltigt BankID kan du hämta ett hos din bank. Försök sedan igen.';
    case 50:
      return 'En identifiering eller underskrift för det här personnumret är redan påbörjad. Försök igen.';
    case 51:
      return 'Åtgärden avbruten.';
    case 52:
      return 'Åtgärden avbruten. Försök igen.';
    case 56:
      if (isThisDevice) {
        return 'BankID-appen verkar inte finnas i din dator eller telefon. Installera den och hämta ett BankID hos din internetbank. ' + 'Installera appen från din appbutik eller https://install.bankid.com.';
      }
      return 'Misslyckades att läsa av QR koden. Starta BankID-appen och läs av QR koden. Kontrollera att BankID-appen är uppdaterad. ' + 'Om du inte har BankID-appen måste du installera den och hämta ett BankID hos din internetbank. Installera appen från din appbutik eller https://install.bankid.com.';
    default:
      logger.warn('Missing error message for errorCode.', {
        errorCode,
        isThisDevice
      });
      return 'Ett okänt fel inträffade.';
  }
};
export const BankIdLoginContainer = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const history = useHistory();
  const [isLoginThisDevice, setIsLoginThisDevice] = useState(null);
  const [isCanceled, setIsCanceled] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const {
    isComplete,
    retailerId,
    rememberRetailerId: storeRememberRetailerId,
    iamParams
  } = useSelector(({
    login
  }) => login);
  const isIamFlow = query.get('iam') === '1';
  const isIamLoginEnabled = shouldUseIamFlowForCurrentHost(svs.core.detect.feature('ft-iam-login'));
  const hasIamRedirectContext = Boolean(iamParams?.redirectUri && iamParams?.state);
  const iamBankIdLoginUrl = getIamLoginUrl('/logga-in/dev-login');
  const [initiateLogin, {
    data: ast,
    error: initiateLoginError,
    isLoading
  }] = bankIdApi.useInitiateLoginMutation();
  const [getAuthenticationStatus, {
    data: statusData = {},
    error: pollError
  }] = bankIdApi.useLazyGetAuthenticateStatusQuery();
  const {
    qrData: statusQrData,
    code,
    jwt,
    session
  } = statusData;
  const queryAutoStartToken = query.get('autoStartToken');
  const queryIsLoginThisDevice = query.get('isLoginThisDevice') === 'true';
  const statusPoller = useRef(null);
  const hasRedirectedRef = useRef(false);
  logger.info('IAM params in store', {
    iamParams
  });
  useEffect(() => {
    if (!isIamLoginEnabled) {
      return;
    }
    if (!hasIamRedirectContext) {
      window.location.assign(iamBankIdLoginUrl);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const hasOidcParamsInUrl = params.has('clientId') || params.has('nonce') || params.has('state') || params.has('redirectUri');
    if (hasOidcParamsInUrl) {
      history.replace({
        pathname: history.location.pathname,
        search: 'iam=1'
      });
    }
  }, [history, hasIamRedirectContext, iamBankIdLoginUrl, isIamLoginEnabled]);
  useEffect(() => {
    if (!initiateLoginError) {
      return;
    }
    const ssnMessage = getRetailerIdIssueProposal(retailerId) || 'Har du angivit rätt ombudsnummer?';
    const errMsg = navigator.onLine ? ssnMessage : 'Inloggningen kunde inte slutföras på grund av obefintlig internetanslutning.';
    logger.info(`Initiate login failed with errorCode ${initiateLoginError.data?.error?.code}: ${errMsg}`);
    setErrorMessage(errMsg);
  }, [initiateLoginError, retailerId]);
  useEffect(() => {
    if (queryAutoStartToken) {
      logger.info('Got autoStartToken from query param, will start polling to get auth status.', {
        queryAutoStartToken
      });
      statusPoller.current = setInterval(() => getAuthenticationStatus(queryAutoStartToken), 1000);
    } else if (statusPoller.current) {
      clearInterval(statusPoller.current);
      statusPoller.current = null;
    }
    return () => {
      if (statusPoller.current) {
        clearInterval(statusPoller.current);
        statusPoller.current = null;
      }
    };
  }, [getAuthenticationStatus, queryAutoStartToken]);
  useEffect(() => {
    if (!ast) {
      return;
    }
    logger.info('Got autoStartToken from API, will update query params to trigger polling', {
      ast
    });
    const params = new URLSearchParams(query.toString());
    params.set('autoStartToken', ast.autoStartToken);
    params.set('isLoginThisDevice', String(isLoginThisDevice));
    history.push({
      search: params.toString()
    });
  }, [ast, history, query, isLoginThisDevice]);
  useEffect(() => {
    if (!isCanceled) {
      return;
    }
    logger.info('Canceled login.');
    const params = new URLSearchParams(query.toString());
    params.delete('autoStartToken');
    params.delete('isLoginThisDevice');
    history.push({
      search: params.toString()
    });
    setIsCanceled(false);
  }, [history, isCanceled, query]);
  useEffect(() => {
    if (isIamFlow) {
      return;
    }
    if (session) {
      dispatch(authenticateComplete({
        isBankIdLogin: true,
        isLoginThisDevice
      }));
    }
  }, [dispatch, isIamFlow, session, isLoginThisDevice]);
  useEffect(() => {
    if (!isIamFlow) {
      return;
    }
    if (hasRedirectedRef.current) {
      return;
    }
    const redirectUri = iamParams?.redirectUri;
    const state = iamParams?.state;
    const returnedCode = code || jwt;
    if (returnedCode && redirectUri && state) {
      hasRedirectedRef.current = true;
      if (statusPoller.current) {
        clearInterval(statusPoller.current);
        statusPoller.current = null;
      }
      const sanitizedRetailerId = sanitizeRetailerId(retailerId);
      if (sanitizedRetailerId) {
        document.cookie = `iam_retailer_id=${sanitizedRetailerId}; Max-Age=300; Path=/; SameSite=Lax`;
      }
      const url = `${redirectUri}?state=${encodeURIComponent(state)}&code=${encodeURIComponent(returnedCode)}`;
      logger.info('IAM success: redirecting to oauth2 callback', {
        url
      });
      window.location.assign(url);
    }
  }, [isIamFlow, iamParams, code, jwt]);
  const handleBankIdAuthenticateStart = ({
    isThisDevice,
    rememberRetailerId
  }) => {
    logger.info(`Will initiate login for retailer ${retailerId}, isThisDevice: ${isThisDevice}`);
    setIsLoginThisDevice(isThisDevice);
    if (isIamFlow) {
      const sanitizedRetailerId = sanitizeRetailerId(retailerId);
      if (sanitizedRetailerId) {
        document.cookie = `iam_retailer_id=${sanitizedRetailerId}; Max-Age=300; Path=/; SameSite=Lax`;
      }
    }
    if (isIamFlow) {
      initiateLogin({
        retailerId,
        iamParams
      });
    } else {
      initiateLogin(retailerId);
    }
    dispatch(setRememberRetailerId(rememberRetailerId));
  };
  const handleChangeRetailerId = retailerId => dispatch(changeRetailerId(retailerId));
  const handleCancelLogin = useCallback(() => {
    dispatch(bankIdApi.util.resetApiState());
    setIsLoginThisDevice(null);
    hasRedirectedRef.current = false;
    setIsCanceled(true);
  }, [dispatch]);
  const handleClearErrorMessage = () => setErrorMessage(null);
  useEffect(() => {
    if (!pollError) {
      return;
    }
    const errorCode = pollError.data?.error?.code;
    let errMsg;
    if (pollError?.status === 'FETCH_ERROR') {
      errMsg = navigator.onLine ? 'Ett nätverksfel inträffade.' : 'Inloggningen kunde inte slutföras på grund av obefintlig internetanslutning.';
      logger.info(`Bank-id polling failed due to FETCH_ERROR': ${errMsg}`, {
        pollError
      });
    } else {
      errMsg = getErrorMessage({
        errorCode,
        isThisDevice: isLoginThisDevice,
        retailerId
      });
      logger.info(`Bank-id polling failed with errorCode '${errorCode}': ${errMsg}`, {
        pollError
      });
    }
    setErrorMessage(errMsg);
    handleCancelLogin();
  }, [pollError, handleCancelLogin, isLoginThisDevice, retailerId]);
  return <BankIdLogin autoStartToken={queryAutoStartToken} errorMessage={errorMessage} isLoading={isLoading} isLoginFinished={isComplete} isLoginThisDevice={queryIsLoginThisDevice} isRememberRetailerIdChecked={storeRememberRetailerId} onBankIdAuthenticateStart={handleBankIdAuthenticateStart} onCancelLogin={handleCancelLogin} onChangeRetailerId={handleChangeRetailerId} onCloseErrorDialog={handleClearErrorMessage} qrData={statusQrData ?? ast?.qrData} retailerId={retailerId} />;
};